import React from 'react'
import BaseLayout from '../components/BaseLayout'
import PageBanner from '../components/PageBanner'
import TopNav from '../components/TopNav'
import Footer from '../components/Footer'
import Slices from '../components/Slices'
import Alerts from '../components/Alerts'
import SubFooter from '@berlitz/sub-footer'
import Head from '../components/Head'
import cleanDeep from 'clean-deep'
import { graphql } from 'gatsby'
import { getSerializedHTML } from '../functions/utils'
import Breadcrumbs from '../components/Breadcrumbs'
import FloatingActionButton from '@berlitz/floating-action-button'

if (typeof window !== 'undefined') {
  require('smooth-scroll')('a[href*="#"]:not([href*="max-level-chart"])', {
    speed: 1000,
    speedAsDuration: true,
    updateURL: false,
    offset: window.outerWidth > 768 ? 90 : 64,
  })
}

const Page = ({ pageContext, data }) => {
  const pageData = data.prismicPage?.data || {}
  const menuData = data.prismicMenu?.data?.body || []
  const menuPhoneNumbersData = data.prismicMenu?.data?.phone_numbers
  const multiplePhoneButtonText = data.prismicMenu?.data?.multiple_phone_button
  const phoneModalFooter = getSerializedHTML(
    data.prismicMenu?.data?.phone_modal_footer?.richText
  )
  const settingsData = data.prismicSettings?.data || {}
  const footerColumns = data.prismicFooter?.data?.body || []
  const footerPhoneNumbersData = data.prismicFooter?.data?.phone_numbers
  const subFooterData = data.prismicSubFooter?.data
  const subFooterLinks = subFooterData?.sub_footer_links?.map((item, index) => (
    <a key={index} href={item?.link?.document?.url}>
      {item?.custom_link_label || item?.link.document?.data?.title}
    </a>
  ))
  const socialLinkData = cleanDeep(data?.prismicFooter?.data?.social_link)
  const footerSocialLinks = (socialLinkData || []).map(item => ({
    title: item.social_platform.toLowerCase(),
    href: item.social_link_url,
  }))
  const courseWidgetData = data?.pirsmicCourseWizards?.edges || {}
  const isHome = pageContext.uid === 'home'
  const disableCtaOnPage = pageData.banner_cta_group
    ? pageData.banner_cta_group[0]?.hide_cta_button
    : false
  const disableCtaGlobal = settingsData?.hide_cta_button_global ?? false
  const disableCta = disableCtaGlobal ? disableCtaGlobal : disableCtaOnPage
  const disableStickyCtaOnPage =
    pageData?.banner_cta_group[0]?.hide_sticky_cta ?? false
  const disableStickyCtaGlobal = settingsData?.hide_sticky_cta_global ?? false
  const disableSticky = disableStickyCtaGlobal
    ? disableStickyCtaGlobal
    : disableStickyCtaOnPage

  return (
    <BaseLayout>
      <Head
        description={pageData.seo_meta_description}
        noFollow={settingsData?.seo_no_index || pageData.seo_no_follow}
        noIndex={settingsData?.seo_no_index || pageData.seo_no_index}
        pageLocale={data.prismicPage?.lang}
        pathName={data.prismicPage?.url}
        region={pageContext.pluginOptions.REGION}
        title={
          pageData.seo_meta_title || `Berlitz - ${settingsData.partner_name}`
        }
        canonical={pageData.seo_canonical}
        imageUrl={pageData.feature_image?.url}
      />
      <Alerts
        region={pageContext.pluginOptions.REGION}
        alertsDismissible={pageContext.alertsDismissible}
        alertsNonDismissible={pageContext.alertsNonDismissible}
        cookieAlertText={getSerializedHTML(
          settingsData?.cookies_banner_text.richText
        )}
        cookieButtonText={settingsData?.cookies_accept_button_text}
        pluginOptions={pageContext.pluginOptions}
      />
      <TopNav
        menu={menuData}
        partnerLogo={settingsData?.partner_logo?.url}
        currentLanguage={pageContext.currentLanguage}
        alternateLanguages={pageContext.alternateLanguages}
        homeUrl={pageContext.homeUrl}
        phoneNumbers={menuPhoneNumbersData}
        multiplePhoneButton={multiplePhoneButtonText}
        phoneModalFooter={phoneModalFooter}
      />
      <PageBanner
        backgroundImageSrc={pageData.feature_image?.url}
        noBanner={!pageData.feature_image?.url}
        title={pageData.title}
        subtitle={pageData.subtitle}
        navigationWidgetData={courseWidgetData}
        showNavWidget={isHome}
        disableCta={disableCta}
        ctaData={
          !disableCtaOnPage && pageData.banner_cta_group
            ? pageData.banner_cta_group[0]
            : null
        }
      />
      {!isHome && (
        <Breadcrumbs
          crumbs={pageContext.parentPages}
          homeUrl={pageContext.homeUrl}
          title={pageContext.pageLabel || pageContext.title}
          lang={pageContext.lang}
        />
      )}
      <Slices
        slices={pageData.body}
        homeUrl={pageContext.homeUrl}
        lang={pageContext.lang}
        pageLinks={pageContext.pageLinks}
      />
      <Footer
        phoneNumbers={footerPhoneNumbersData}
        homeUrl={pageContext.homeUrl}
        columns={footerColumns}
        partnerLogo={settingsData?.partner_logo?.url}
        socialLinks={footerSocialLinks}
      />
      <SubFooter
        copyrightText={
          subFooterData?.copyright_text || '© 2021 Berlitz Corporation'
        }
        links={subFooterLinks}
      />
      {!disableSticky && (
        <FloatingActionButton
          text={
            pageData?.banner_cta_group && pageData?.banner_cta_group[0]?.label
          }
          href={
            pageData?.banner_cta_group[0]?.one_page_link
              ? pageData?.banner_cta_group[0]?.one_page_link
              : pageData?.banner_cta_group[0]?.normal_link?.document?.url
          }
          as="a"
        />
      )}
    </BaseLayout>
  )
}

export const query = graphql`
  query PageQuery($uid: String, $lang: String) {
    prismicPage(uid: { eq: $uid }, lang: { eq: $lang }) {
      url
      lang
      data {
        title
        subtitle
        feature_image {
          url
        }
        banner_cta_group {
          hide_cta_button
          hide_sticky_cta
          label
          normal_link {
            document {
              ... on PrismicPage {
                url
                data {
                  title
                }
              }
            }
          }
          one_page_link
        }
        seo_meta_title
        seo_meta_description
        seo_no_follow
        seo_no_index
        seo_canonical
        body {
          ...Slices
        }
      }
    }
    prismicMenu(lang: { eq: $lang }) {
      data {
        body {
          ...Menu
        }
        phone_numbers {
          ...PhoneNumbers
        }
        multiple_phone_button
        phone_modal_footer {
          richText
        }
      }
    }
   pirsmicCourseWizards:  allPrismicCourseWizard(filter: { lang: { eq: $lang } }) {
      edges {
        node {
          data {
            title
            submitButtonLabel: submit_button_label
            sort_order
            identifierText: identifier_text
            audienceOption: audience_option {
              label
              value
            }
            intentText: intent_text
            body {
              ...CourseWizard
            }
          }
        }
      }
    }
    prismicSubFooter(lang: { eq: $lang }) {
      data {
        copyright_text
        sub_footer_links {
          custom_link_label
          link {
            document {
              ... on PrismicPage {
                url
                data {
                  title
                }
              }
            }
          }
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      data {
        body {
          ...Columns
        }
        social_link {
          social_platform
          social_link_title
          social_link_url
        }
        phone_numbers {
          ...FooterPhoneNumbers
        }
      }
    }
    prismicSettings(lang: { eq: $lang }) {
      data {
        partner_name
        partner_logo {
          url
        }
        cookies_banner_text {
          richText
        }
        cookies_accept_button_text
        seo_no_index
        hide_sticky_cta_global
        hide_cta_button_global
      }
    }
  }
`

export default Page
